import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import MostCommonBrands from './components/MostCommonBrands';
import LabelWarningInfo from './components/LabelWarningInfo';
import DrugFoodInteractions from './components/DrugFoodInteractions';
import DrugDiseaseContraIndication from './components/DrugDiseaseContraIndication';
import GeriatricPrecautions from './components/GeriatricPrecautions';
import PediatricPrecautions from './components/PediatricPrecautions';
import LactationPrecautions from './components/LactationPrecautions';
import PregnancyPrecautions from './components/PregnancyPrecautions';
import BasicDrugInfo from './components/BasicDrugInfo';
import Indications from './components/Indications';
import SideEffects from './components/SideEffects';
import PatientEducation from './components/PatientEducation/PatientEducation';
import Ingredients from './components/Ingredients';

import drugsApiService from '../../../services/drugsapi.service';
import TherapeuticAlternatives from './components/TherapeuticAlternatives';
import { useLoader } from '../../../loader/LoaderContext';
import DrugSearchForm from '../DrugSearchForm';
import DrugDrugInteractions from './components/DrugDrugInteractions';
import Sidebar from '../../../components/Sidebar/Sidebar';
import { capitalizeFirstLetter } from '../../../utils/textFormatUtils';

import { IconCreditCardPay, IconInputSearch } from '@tabler/icons-react';
import useScrollSpy from '../../../hooks/useScrollSpy';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

const sidebarItems = [
  {
    path: '/landing/coverage-verification',
    label: 'Coverage Verification',
    icon: <IconCreditCardPay size={24} />,
    matchSubpaths: false,
  },
  {
    path: '/landing/drug-search',
    label: 'Drug Search',
    icon: <IconInputSearch size={24} />,
    matchSubpaths: true,
  },
];

function DrugInformation() {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const { drugId } = useParams();
  const [drugData, setDrugData] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  const [activeSection, setActiveSection] = useState('general');

  //Refs for the components
  const basicDrugInfoRef = useRef(null);
  const indicationsRef = useRef(null);
  const sideEffectsRef = useRef(null);
  const pregnancyPrecautionsRef = useRef(null);
  const lactationPrecautionsRef = useRef(null);
  const pediatricPrecautionsRef = useRef(null);
  const geriatricPrecautionsRef = useRef(null);
  const labelWarningInfoRef = useRef(null);
  const drugDrugInteractionsRef = useRef(null);
  const drugFoodInteractionsRef = useRef(null);
  const drugDiseaseContraIndicationRef = useRef(null);
  const ingredientsRef = useRef(null);
  const mostCommonBrandsRef = useRef(null);
  const therapeuticAlternativesRef = useRef(null);

  useScrollSpy(
    [
      'general-info-section',
      'indications-section',
      'side-effects-section',
      'pregnancy-precautions-section',
      'lactation-precautions-section',
      'pediatric-precautions-section',
      'geriatric-precautions-section',
      'label-warning-info-section',
      'drug-drug-interactions-section',
      'drug-food-interactions-section',
      'drug-disease-contra-indication-section',
      'ingredients-section',
      'most-common-brands-section',
      'therapeutic-alternatives-section',

      'disclaimer-section',
      'warning-section',
      'uses-section',
      'how-to-use-section',
      'education-side-effects-section',
      'precautions-section',
      'drug-interactions-section',
      'overdose-section',
      'missed-dose-section',
      'storage-section',
      'notes-section',
    ],
    120,
  );

  useEffect(() => {
    const fetchDrugData = async () => {
      try {
        showLoader();
        const drugResponseData = await drugsApiService.getDrugInfo(drugId);

        sendToKinesis('drug_events', 'DRUG_ACTIVITY', {
          userid: user.userId,
          searchterm: drugResponseData?.Basic_Drug_Info?.DispensableGenericDesc,
        });

        if (!drugResponseData) {
          navigate('/landing/drug-search');
        }
        setDrugData(drugResponseData);
        window.scrollTo(0, 0);
      } catch (err) {
        navigate('/landing/drug-search');
      } finally {
        hideLoader();
      }
    };

    if (drugId) {
      fetchDrugData();
    }
  }, [drugId, navigate]);
  if (!drugData) {
    return <></>;
  }
  return (
    <>
      <div className="">
        <div className="blur-box" />
        <div className="fixed-container-left">
          <div className="d-flex ">
            <Sidebar items={sidebarItems} />
          </div>
        </div>
        <div className="drugresult-box ">
          <div style={{ width: '500px' }}>
            <DrugSearchForm />
          </div>
          <h1 className="mt-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="currentColor"
              className="icon icon-tabler icons-tabler-filled icon-tabler-pill"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M20.207 3.793a5.95 5.95 0 0 1 0 8.414l-8 8a5.95 5.95 0 0 1 -8.414 -8.414l8 -8a5.95 5.95 0 0 1 8.414 0m-7 1.414l-4.294 4.293l5.586 5.586l4.294 -4.292a3.95 3.95 0 1 0 -5.586 -5.586"></path>
            </svg>
            {capitalizeFirstLetter(
              drugData.Basic_Drug_Info.DispensableGenericDesc,
            )}
          </h1>
        </div>
        <div className="right-sidebar">
          <div className="accordion ps-accordion" id="accordionExample">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseOne"
                  aria-expanded="true"
                  aria-controls="collapseOne"
                  onClick={() => {
                    setActiveSection('general');
                    window.scrollTo(0, 0);
                  }}
                >
                  General Information
                </button>
              </h2>
              <div
                id="collapseOne"
                className="accordion-collapse collapse show"
                data-bs-parent="#accordionExample"
              >
                <div className="accordion-body">
                  <div className="page-links">
                    <a href="#general-info-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-info-circle"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                        <path d="M12 9h.01" />
                        <path d="M11 12h1v4h1" />
                      </svg>
                      General Information
                    </a>
                    <a href="#indications-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                        <path d="M13.5 6.5l4 4" />
                      </svg>
                      Indications
                    </a>
                    <a href="#side-effects-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-bolt"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />
                      </svg>
                      Side Effects
                    </a>
                    <a href="#pregnancy-precautions-section">
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <mask
                          id="path-1-outside-1_55_2881"
                          maskUnits="userSpaceOnUse"
                          x={0}
                          y={1}
                          width={18}
                          height={16}
                          fill="black"
                        >
                          <rect fill="white" y={1} width={18} height={16} />
                          <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.95723 2.05915C4.30455 2.18453 3.69944 2.43413 3.12569 2.81461C2.72629 3.0795 2.14375 3.6659 1.86495 4.08364C0.388356 6.29626 0.81882 9.09118 3.02996 11.6479C3.34939 12.0172 3.45003 12.0752 3.62414 11.9901C3.76167 11.9228 3.83072 11.7963 3.80177 11.6645C3.79022 11.6119 3.66022 11.4288 3.50705 11.2496C3.1407 10.8208 2.8971 10.495 2.63293 10.0803C1.21594 7.85587 1.29146 5.48613 2.83041 3.88275C3.6432 3.03595 4.6475 2.61123 5.82758 2.61525C6.52225 2.61763 7.02506 2.74685 7.71759 3.10101C8.01222 3.25167 8.1029 3.28473 8.17682 3.2685C8.40349 3.21873 8.49713 2.93252 8.34104 2.76638C8.24919 2.6686 7.68936 2.37962 7.36619 2.26319C6.85807 2.0801 6.51901 2.02241 5.87468 2.00948C5.37665 1.99949 5.22508 2.00769 4.95723 2.05915ZM11.7151 2.01676C11.2308 2.07746 10.6352 2.26033 10.1765 2.48924C9.19261 2.98021 8.71561 3.57669 8.71677 4.31459C8.71743 4.7416 8.83217 5.01887 9.25089 5.60536C9.6156 6.11621 9.70962 6.34851 9.74186 6.81872C9.76318 7.12952 9.74325 7.17687 9.61981 7.1087C9.58009 7.08675 9.43722 7.06873 9.2973 7.06801C8.99404 7.06638 8.80783 7.14419 8.60273 7.35827L8.46965 7.49715L8.36365 7.4163C8.21246 7.301 8.00452 7.21553 7.81364 7.19028L7.64879 7.16846L7.64832 6.70936C7.64763 6.00483 7.53858 5.67089 7.1842 5.288C6.66248 4.72433 5.75442 4.47797 4.9551 4.68326C3.83223 4.97164 3.10447 6.11592 3.32876 7.24046C3.39476 7.57129 3.60304 8.01212 3.81204 8.26316C4.06512 8.56721 4.07875 8.60806 4.10412 9.13907C4.14557 10.0067 4.35068 10.6683 4.77684 11.3088C5.42968 12.2899 6.48146 12.9603 7.67934 13.1589C8.14724 13.2364 8.82831 13.2104 9.2773 13.0978C9.68921 12.9945 10.1339 12.8093 10.4088 12.6266C10.6808 12.4458 11.0277 12.0813 11.1918 11.8039C11.3572 11.5244 11.5061 11.0743 11.5312 10.7785L11.551 10.5442L11.7591 10.5293C12.139 10.5021 12.4142 10.3146 12.5711 9.97582C12.6399 9.8272 12.6471 9.77816 12.6328 9.55192C12.6146 9.26476 12.4254 8.61877 12.2602 8.27955C12.0609 7.87047 11.6539 7.57264 11.1554 7.471C10.9141 7.42179 10.7478 7.43021 10.407 7.50893L10.3235 7.52821L10.3407 7.09219C10.37 6.35312 10.2644 5.98728 9.84828 5.38675C9.37794 4.70788 9.29356 4.50136 9.34741 4.16079C9.39477 3.86121 9.66405 3.50974 10.0171 3.2868C10.7389 2.83094 11.4417 2.61798 12.2332 2.61525C13.4133 2.61123 14.4176 3.03595 15.2304 3.88275C16.4863 5.19122 16.7786 7.00744 16.0364 8.89085C15.3065 10.7428 13.5001 12.6433 10.8987 14.2959C10.4153 14.603 9.57365 15.088 9.24254 15.2502L9.03081 15.3539L8.85004 15.2658C8.04855 14.8754 6.67306 14.0151 5.80256 13.3597C5.52087 13.1476 5.45439 13.1112 5.3493 13.1112C5.25506 13.1112 5.2046 13.1327 5.13436 13.203C5.03828 13.2991 5.02054 13.3946 5.0731 13.5328C5.12566 13.6711 6.37065 14.5564 7.27199 15.0965C8.09756 15.5912 8.89321 16 9.03034 16C9.21409 16 10.3827 15.3674 11.3687 14.7342C13.3243 13.4784 14.8631 12.0522 15.8303 10.5992C17.012 8.82369 17.3548 6.942 16.7967 5.29381C16.1224 3.30247 14.3026 1.9793 12.267 2.00025C12.0153 2.00282 11.7669 2.01026 11.7151 2.01676ZM12.1704 3.50613C11.9125 3.60734 11.9228 3.98106 12.186 4.06791C12.268 4.09498 12.3144 4.0925 12.4007 4.05645C12.5272 4.00361 12.5786 3.92523 12.5786 3.78534C12.5786 3.64649 12.5273 3.56714 12.4044 3.51577C12.2825 3.46484 12.2763 3.46459 12.1704 3.50613ZM13.2169 3.70628C13.098 3.75708 13.0433 3.8483 13.0433 3.99572C13.0433 4.13395 13.132 4.23003 13.3431 4.32078C14.021 4.61201 14.601 5.26749 14.8558 6.03045C14.9189 6.21951 14.9961 6.67105 14.9963 6.85229C14.9965 7.04744 15.1147 7.17656 15.2933 7.17656C15.3964 7.17656 15.4466 7.1574 15.5103 7.09379C15.5863 7.01771 15.593 6.98998 15.593 6.75184C15.593 5.54485 14.7706 4.2803 13.6634 3.78487C13.4051 3.66929 13.3332 3.6566 13.2169 3.70628ZM5.08968 5.28837C4.10849 5.57032 3.62876 6.65249 4.08525 7.55402C4.12478 7.63208 4.23425 7.79458 4.32845 7.91512C4.64581 8.32109 4.68691 8.44591 4.72638 9.12337C4.77307 9.92448 4.91937 10.3923 5.30364 10.9695C5.90712 11.876 6.91007 12.4716 8.00186 12.572C8.84812 12.6498 9.78746 12.3922 10.2748 11.9486C10.7054 11.5567 10.9197 11.0784 10.917 10.515C10.9155 10.1944 10.8932 10.0722 10.7594 9.64919C10.6279 9.23327 10.6247 9.12761 10.7403 9.01205C10.7958 8.95648 10.8516 8.93497 10.9402 8.93497C11.1441 8.93497 11.2075 9.01268 11.3356 9.41957C11.4632 9.82438 11.531 9.90837 11.7307 9.90837C11.8147 9.90837 11.8707 9.88548 11.9286 9.82758C12.0599 9.69629 12.059 9.61403 11.9216 9.16632C11.7544 8.62175 11.7006 8.50435 11.5449 8.34433C11.1084 7.89568 10.3587 8.0042 10.0509 8.56061C9.96625 8.7136 9.95692 8.75793 9.95871 8.99777C9.96044 9.22649 9.98173 9.32979 10.1076 9.71997C10.2574 10.1843 10.2828 10.3563 10.2147 10.4449C10.1126 10.5779 9.90351 10.6057 9.77367 10.5036C9.71329 10.4561 9.66283 10.3478 9.57981 10.0875C9.51807 9.89399 9.46096 9.71667 9.45292 9.69341C9.44306 9.66502 9.38092 9.70361 9.26311 9.81125C9.05624 10.0003 8.71611 10.171 8.43998 10.2244C8.30126 10.2512 7.99793 10.2579 7.42579 10.2469L6.60954 10.2312L6.51685 10.1424C6.44287 10.0715 6.42419 10.0269 6.42419 9.92099C6.42419 9.81125 6.44095 9.77423 6.5214 9.70656L6.61862 9.62476L7.47383 9.62681C8.28207 9.62872 8.33922 9.62505 8.51487 9.55939C8.82561 9.44327 9.02651 9.20922 9.33699 8.60137C9.63378 8.02034 9.65379 7.92074 9.50382 7.77074C9.38315 7.65007 9.20034 7.64291 9.08962 7.75448C9.04842 7.79602 8.89601 8.05799 8.75091 8.33664C8.56618 8.6914 8.45794 8.86407 8.3898 8.91267C8.29633 8.97933 8.26464 8.98207 7.58599 8.98207C6.93595 8.98207 6.87098 8.97704 6.77292 8.91927C6.5977 8.81602 6.48008 8.64499 6.46315 8.46886C6.43986 8.22645 6.49531 8.1158 6.78384 7.82871L7.04484 7.56906L7.03319 6.90397C7.01966 6.13115 6.9876 5.99874 6.75028 5.73479C6.37074 5.31265 5.66524 5.12299 5.08968 5.28837ZM7.57067 7.86614C7.55864 7.9041 7.45653 8.03293 7.34377 8.15244L7.13879 8.36976H7.59312H8.04745L8.1046 8.24303C8.16046 8.11919 8.16033 8.11439 8.09957 8.03303C7.99928 7.89867 7.88542 7.83015 7.73417 7.8131C7.60879 7.79897 7.59007 7.80503 7.57067 7.86614ZM4.26696 12.4048C4.17531 12.469 4.12146 12.6398 4.16052 12.7425C4.21299 12.8806 4.31884 12.9542 4.46482 12.9542C4.73247 12.9542 4.85898 12.6402 4.6682 12.4494C4.56316 12.3443 4.38201 12.3242 4.26696 12.4048Z"
                          />
                        </mask>
                        <path
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.95723 2.05915C4.30455 2.18453 3.69944 2.43413 3.12569 2.81461C2.72629 3.0795 2.14375 3.6659 1.86495 4.08364C0.388356 6.29626 0.81882 9.09118 3.02996 11.6479C3.34939 12.0172 3.45003 12.0752 3.62414 11.9901C3.76167 11.9228 3.83072 11.7963 3.80177 11.6645C3.79022 11.6119 3.66022 11.4288 3.50705 11.2496C3.1407 10.8208 2.8971 10.495 2.63293 10.0803C1.21594 7.85587 1.29146 5.48613 2.83041 3.88275C3.6432 3.03595 4.6475 2.61123 5.82758 2.61525C6.52225 2.61763 7.02506 2.74685 7.71759 3.10101C8.01222 3.25167 8.1029 3.28473 8.17682 3.2685C8.40349 3.21873 8.49713 2.93252 8.34104 2.76638C8.24919 2.6686 7.68936 2.37962 7.36619 2.26319C6.85807 2.0801 6.51901 2.02241 5.87468 2.00948C5.37665 1.99949 5.22508 2.00769 4.95723 2.05915ZM11.7151 2.01676C11.2308 2.07746 10.6352 2.26033 10.1765 2.48924C9.19261 2.98021 8.71561 3.57669 8.71677 4.31459C8.71743 4.7416 8.83217 5.01887 9.25089 5.60536C9.6156 6.11621 9.70962 6.34851 9.74186 6.81872C9.76318 7.12952 9.74325 7.17687 9.61981 7.1087C9.58009 7.08675 9.43722 7.06873 9.2973 7.06801C8.99404 7.06638 8.80783 7.14419 8.60273 7.35827L8.46965 7.49715L8.36365 7.4163C8.21246 7.301 8.00452 7.21553 7.81364 7.19028L7.64879 7.16846L7.64832 6.70936C7.64763 6.00483 7.53858 5.67089 7.1842 5.288C6.66248 4.72433 5.75442 4.47797 4.9551 4.68326C3.83223 4.97164 3.10447 6.11592 3.32876 7.24046C3.39476 7.57129 3.60304 8.01212 3.81204 8.26316C4.06512 8.56721 4.07875 8.60806 4.10412 9.13907C4.14557 10.0067 4.35068 10.6683 4.77684 11.3088C5.42968 12.2899 6.48146 12.9603 7.67934 13.1589C8.14724 13.2364 8.82831 13.2104 9.2773 13.0978C9.68921 12.9945 10.1339 12.8093 10.4088 12.6266C10.6808 12.4458 11.0277 12.0813 11.1918 11.8039C11.3572 11.5244 11.5061 11.0743 11.5312 10.7785L11.551 10.5442L11.7591 10.5293C12.139 10.5021 12.4142 10.3146 12.5711 9.97582C12.6399 9.8272 12.6471 9.77816 12.6328 9.55192C12.6146 9.26476 12.4254 8.61877 12.2602 8.27955C12.0609 7.87047 11.6539 7.57264 11.1554 7.471C10.9141 7.42179 10.7478 7.43021 10.407 7.50893L10.3235 7.52821L10.3407 7.09219C10.37 6.35312 10.2644 5.98728 9.84828 5.38675C9.37794 4.70788 9.29356 4.50136 9.34741 4.16079C9.39477 3.86121 9.66405 3.50974 10.0171 3.2868C10.7389 2.83094 11.4417 2.61798 12.2332 2.61525C13.4133 2.61123 14.4176 3.03595 15.2304 3.88275C16.4863 5.19122 16.7786 7.00744 16.0364 8.89085C15.3065 10.7428 13.5001 12.6433 10.8987 14.2959C10.4153 14.603 9.57365 15.088 9.24254 15.2502L9.03081 15.3539L8.85004 15.2658C8.04855 14.8754 6.67306 14.0151 5.80256 13.3597C5.52087 13.1476 5.45439 13.1112 5.3493 13.1112C5.25506 13.1112 5.2046 13.1327 5.13436 13.203C5.03828 13.2991 5.02054 13.3946 5.0731 13.5328C5.12566 13.6711 6.37065 14.5564 7.27199 15.0965C8.09756 15.5912 8.89321 16 9.03034 16C9.21409 16 10.3827 15.3674 11.3687 14.7342C13.3243 13.4784 14.8631 12.0522 15.8303 10.5992C17.012 8.82369 17.3548 6.942 16.7967 5.29381C16.1224 3.30247 14.3026 1.9793 12.267 2.00025C12.0153 2.00282 11.7669 2.01026 11.7151 2.01676ZM12.1704 3.50613C11.9125 3.60734 11.9228 3.98106 12.186 4.06791C12.268 4.09498 12.3144 4.0925 12.4007 4.05645C12.5272 4.00361 12.5786 3.92523 12.5786 3.78534C12.5786 3.64649 12.5273 3.56714 12.4044 3.51577C12.2825 3.46484 12.2763 3.46459 12.1704 3.50613ZM13.2169 3.70628C13.098 3.75708 13.0433 3.8483 13.0433 3.99572C13.0433 4.13395 13.132 4.23003 13.3431 4.32078C14.021 4.61201 14.601 5.26749 14.8558 6.03045C14.9189 6.21951 14.9961 6.67105 14.9963 6.85229C14.9965 7.04744 15.1147 7.17656 15.2933 7.17656C15.3964 7.17656 15.4466 7.1574 15.5103 7.09379C15.5863 7.01771 15.593 6.98998 15.593 6.75184C15.593 5.54485 14.7706 4.2803 13.6634 3.78487C13.4051 3.66929 13.3332 3.6566 13.2169 3.70628ZM5.08968 5.28837C4.10849 5.57032 3.62876 6.65249 4.08525 7.55402C4.12478 7.63208 4.23425 7.79458 4.32845 7.91512C4.64581 8.32109 4.68691 8.44591 4.72638 9.12337C4.77307 9.92448 4.91937 10.3923 5.30364 10.9695C5.90712 11.876 6.91007 12.4716 8.00186 12.572C8.84812 12.6498 9.78746 12.3922 10.2748 11.9486C10.7054 11.5567 10.9197 11.0784 10.917 10.515C10.9155 10.1944 10.8932 10.0722 10.7594 9.64919C10.6279 9.23327 10.6247 9.12761 10.7403 9.01205C10.7958 8.95648 10.8516 8.93497 10.9402 8.93497C11.1441 8.93497 11.2075 9.01268 11.3356 9.41957C11.4632 9.82438 11.531 9.90837 11.7307 9.90837C11.8147 9.90837 11.8707 9.88548 11.9286 9.82758C12.0599 9.69629 12.059 9.61403 11.9216 9.16632C11.7544 8.62175 11.7006 8.50435 11.5449 8.34433C11.1084 7.89568 10.3587 8.0042 10.0509 8.56061C9.96625 8.7136 9.95692 8.75793 9.95871 8.99777C9.96044 9.22649 9.98173 9.32979 10.1076 9.71997C10.2574 10.1843 10.2828 10.3563 10.2147 10.4449C10.1126 10.5779 9.90351 10.6057 9.77367 10.5036C9.71329 10.4561 9.66283 10.3478 9.57981 10.0875C9.51807 9.89399 9.46096 9.71667 9.45292 9.69341C9.44306 9.66502 9.38092 9.70361 9.26311 9.81125C9.05624 10.0003 8.71611 10.171 8.43998 10.2244C8.30126 10.2512 7.99793 10.2579 7.42579 10.2469L6.60954 10.2312L6.51685 10.1424C6.44287 10.0715 6.42419 10.0269 6.42419 9.92099C6.42419 9.81125 6.44095 9.77423 6.5214 9.70656L6.61862 9.62476L7.47383 9.62681C8.28207 9.62872 8.33922 9.62505 8.51487 9.55939C8.82561 9.44327 9.02651 9.20922 9.33699 8.60137C9.63378 8.02034 9.65379 7.92074 9.50382 7.77074C9.38315 7.65007 9.20034 7.64291 9.08962 7.75448C9.04842 7.79602 8.89601 8.05799 8.75091 8.33664C8.56618 8.6914 8.45794 8.86407 8.3898 8.91267C8.29633 8.97933 8.26464 8.98207 7.58599 8.98207C6.93595 8.98207 6.87098 8.97704 6.77292 8.91927C6.5977 8.81602 6.48008 8.64499 6.46315 8.46886C6.43986 8.22645 6.49531 8.1158 6.78384 7.82871L7.04484 7.56906L7.03319 6.90397C7.01966 6.13115 6.9876 5.99874 6.75028 5.73479C6.37074 5.31265 5.66524 5.12299 5.08968 5.28837ZM7.57067 7.86614C7.55864 7.9041 7.45653 8.03293 7.34377 8.15244L7.13879 8.36976H7.59312H8.04745L8.1046 8.24303C8.16046 8.11919 8.16033 8.11439 8.09957 8.03303C7.99928 7.89867 7.88542 7.83015 7.73417 7.8131C7.60879 7.79897 7.59007 7.80503 7.57067 7.86614ZM4.26696 12.4048C4.17531 12.469 4.12146 12.6398 4.16052 12.7425C4.21299 12.8806 4.31884 12.9542 4.46482 12.9542C4.73247 12.9542 4.85898 12.6402 4.6682 12.4494C4.56316 12.3443 4.38201 12.3242 4.26696 12.4048Z"
                        />
                        <path
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.95723 2.05915C4.30455 2.18453 3.69944 2.43413 3.12569 2.81461C2.72629 3.0795 2.14375 3.6659 1.86495 4.08364C0.388356 6.29626 0.81882 9.09118 3.02996 11.6479C3.34939 12.0172 3.45003 12.0752 3.62414 11.9901C3.76167 11.9228 3.83072 11.7963 3.80177 11.6645C3.79022 11.6119 3.66022 11.4288 3.50705 11.2496C3.1407 10.8208 2.8971 10.495 2.63293 10.0803C1.21594 7.85587 1.29146 5.48613 2.83041 3.88275C3.6432 3.03595 4.6475 2.61123 5.82758 2.61525C6.52225 2.61763 7.02506 2.74685 7.71759 3.10101C8.01222 3.25167 8.1029 3.28473 8.17682 3.2685C8.40349 3.21873 8.49713 2.93252 8.34104 2.76638C8.24919 2.6686 7.68936 2.37962 7.36619 2.26319C6.85807 2.0801 6.51901 2.02241 5.87468 2.00948C5.37665 1.99949 5.22508 2.00769 4.95723 2.05915ZM11.7151 2.01676C11.2308 2.07746 10.6352 2.26033 10.1765 2.48924C9.19261 2.98021 8.71561 3.57669 8.71677 4.31459C8.71743 4.7416 8.83217 5.01887 9.25089 5.60536C9.6156 6.11621 9.70962 6.34851 9.74186 6.81872C9.76318 7.12952 9.74325 7.17687 9.61981 7.1087C9.58009 7.08675 9.43722 7.06873 9.2973 7.06801C8.99404 7.06638 8.80783 7.14419 8.60273 7.35827L8.46965 7.49715L8.36365 7.4163C8.21246 7.301 8.00452 7.21553 7.81364 7.19028L7.64879 7.16846L7.64832 6.70936C7.64763 6.00483 7.53858 5.67089 7.1842 5.288C6.66248 4.72433 5.75442 4.47797 4.9551 4.68326C3.83223 4.97164 3.10447 6.11592 3.32876 7.24046C3.39476 7.57129 3.60304 8.01212 3.81204 8.26316C4.06512 8.56721 4.07875 8.60806 4.10412 9.13907C4.14557 10.0067 4.35068 10.6683 4.77684 11.3088C5.42968 12.2899 6.48146 12.9603 7.67934 13.1589C8.14724 13.2364 8.82831 13.2104 9.2773 13.0978C9.68921 12.9945 10.1339 12.8093 10.4088 12.6266C10.6808 12.4458 11.0277 12.0813 11.1918 11.8039C11.3572 11.5244 11.5061 11.0743 11.5312 10.7785L11.551 10.5442L11.7591 10.5293C12.139 10.5021 12.4142 10.3146 12.5711 9.97582C12.6399 9.8272 12.6471 9.77816 12.6328 9.55192C12.6146 9.26476 12.4254 8.61877 12.2602 8.27955C12.0609 7.87047 11.6539 7.57264 11.1554 7.471C10.9141 7.42179 10.7478 7.43021 10.407 7.50893L10.3235 7.52821L10.3407 7.09219C10.37 6.35312 10.2644 5.98728 9.84828 5.38675C9.37794 4.70788 9.29356 4.50136 9.34741 4.16079C9.39477 3.86121 9.66405 3.50974 10.0171 3.2868C10.7389 2.83094 11.4417 2.61798 12.2332 2.61525C13.4133 2.61123 14.4176 3.03595 15.2304 3.88275C16.4863 5.19122 16.7786 7.00744 16.0364 8.89085C15.3065 10.7428 13.5001 12.6433 10.8987 14.2959C10.4153 14.603 9.57365 15.088 9.24254 15.2502L9.03081 15.3539L8.85004 15.2658C8.04855 14.8754 6.67306 14.0151 5.80256 13.3597C5.52087 13.1476 5.45439 13.1112 5.3493 13.1112C5.25506 13.1112 5.2046 13.1327 5.13436 13.203C5.03828 13.2991 5.02054 13.3946 5.0731 13.5328C5.12566 13.6711 6.37065 14.5564 7.27199 15.0965C8.09756 15.5912 8.89321 16 9.03034 16C9.21409 16 10.3827 15.3674 11.3687 14.7342C13.3243 13.4784 14.8631 12.0522 15.8303 10.5992C17.012 8.82369 17.3548 6.942 16.7967 5.29381C16.1224 3.30247 14.3026 1.9793 12.267 2.00025C12.0153 2.00282 11.7669 2.01026 11.7151 2.01676ZM12.1704 3.50613C11.9125 3.60734 11.9228 3.98106 12.186 4.06791C12.268 4.09498 12.3144 4.0925 12.4007 4.05645C12.5272 4.00361 12.5786 3.92523 12.5786 3.78534C12.5786 3.64649 12.5273 3.56714 12.4044 3.51577C12.2825 3.46484 12.2763 3.46459 12.1704 3.50613ZM13.2169 3.70628C13.098 3.75708 13.0433 3.8483 13.0433 3.99572C13.0433 4.13395 13.132 4.23003 13.3431 4.32078C14.021 4.61201 14.601 5.26749 14.8558 6.03045C14.9189 6.21951 14.9961 6.67105 14.9963 6.85229C14.9965 7.04744 15.1147 7.17656 15.2933 7.17656C15.3964 7.17656 15.4466 7.1574 15.5103 7.09379C15.5863 7.01771 15.593 6.98998 15.593 6.75184C15.593 5.54485 14.7706 4.2803 13.6634 3.78487C13.4051 3.66929 13.3332 3.6566 13.2169 3.70628ZM5.08968 5.28837C4.10849 5.57032 3.62876 6.65249 4.08525 7.55402C4.12478 7.63208 4.23425 7.79458 4.32845 7.91512C4.64581 8.32109 4.68691 8.44591 4.72638 9.12337C4.77307 9.92448 4.91937 10.3923 5.30364 10.9695C5.90712 11.876 6.91007 12.4716 8.00186 12.572C8.84812 12.6498 9.78746 12.3922 10.2748 11.9486C10.7054 11.5567 10.9197 11.0784 10.917 10.515C10.9155 10.1944 10.8932 10.0722 10.7594 9.64919C10.6279 9.23327 10.6247 9.12761 10.7403 9.01205C10.7958 8.95648 10.8516 8.93497 10.9402 8.93497C11.1441 8.93497 11.2075 9.01268 11.3356 9.41957C11.4632 9.82438 11.531 9.90837 11.7307 9.90837C11.8147 9.90837 11.8707 9.88548 11.9286 9.82758C12.0599 9.69629 12.059 9.61403 11.9216 9.16632C11.7544 8.62175 11.7006 8.50435 11.5449 8.34433C11.1084 7.89568 10.3587 8.0042 10.0509 8.56061C9.96625 8.7136 9.95692 8.75793 9.95871 8.99777C9.96044 9.22649 9.98173 9.32979 10.1076 9.71997C10.2574 10.1843 10.2828 10.3563 10.2147 10.4449C10.1126 10.5779 9.90351 10.6057 9.77367 10.5036C9.71329 10.4561 9.66283 10.3478 9.57981 10.0875C9.51807 9.89399 9.46096 9.71667 9.45292 9.69341C9.44306 9.66502 9.38092 9.70361 9.26311 9.81125C9.05624 10.0003 8.71611 10.171 8.43998 10.2244C8.30126 10.2512 7.99793 10.2579 7.42579 10.2469L6.60954 10.2312L6.51685 10.1424C6.44287 10.0715 6.42419 10.0269 6.42419 9.92099C6.42419 9.81125 6.44095 9.77423 6.5214 9.70656L6.61862 9.62476L7.47383 9.62681C8.28207 9.62872 8.33922 9.62505 8.51487 9.55939C8.82561 9.44327 9.02651 9.20922 9.33699 8.60137C9.63378 8.02034 9.65379 7.92074 9.50382 7.77074C9.38315 7.65007 9.20034 7.64291 9.08962 7.75448C9.04842 7.79602 8.89601 8.05799 8.75091 8.33664C8.56618 8.6914 8.45794 8.86407 8.3898 8.91267C8.29633 8.97933 8.26464 8.98207 7.58599 8.98207C6.93595 8.98207 6.87098 8.97704 6.77292 8.91927C6.5977 8.81602 6.48008 8.64499 6.46315 8.46886C6.43986 8.22645 6.49531 8.1158 6.78384 7.82871L7.04484 7.56906L7.03319 6.90397C7.01966 6.13115 6.9876 5.99874 6.75028 5.73479C6.37074 5.31265 5.66524 5.12299 5.08968 5.28837ZM7.57067 7.86614C7.55864 7.9041 7.45653 8.03293 7.34377 8.15244L7.13879 8.36976H7.59312H8.04745L8.1046 8.24303C8.16046 8.11919 8.16033 8.11439 8.09957 8.03303C7.99928 7.89867 7.88542 7.83015 7.73417 7.8131C7.60879 7.79897 7.59007 7.80503 7.57067 7.86614ZM4.26696 12.4048C4.17531 12.469 4.12146 12.6398 4.16052 12.7425C4.21299 12.8806 4.31884 12.9542 4.46482 12.9542C4.73247 12.9542 4.85898 12.6402 4.6682 12.4494C4.56316 12.3443 4.38201 12.3242 4.26696 12.4048Z"
                          strokeWidth="0.2"
                          mask="url(#path-1-outside-1_55_2881)"
                        />
                      </svg>
                      Pregnancy Precautions
                    </a>
                    <a href="#lactation-precautions-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-baby-bottle"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M5 10h14" />
                        <path d="M12 2v2" />
                        <path d="M12 4a5 5 0 0 1 5 5v11a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2v-11a5 5 0 0 1 5 -5z"></path>
                      </svg>
                      Lactation Precautions
                    </a>
                    <a href="#pediatric-precautions-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-directions"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 21v-4" />
                        <path d="M12 13v-4" />
                        <path d="M12 5v-2" />
                        <path d="M10 21h4" />
                        <path d="M8 5v4h11l2 -2l-2 -2z" />
                        <path d="M14 13v4h-8l-2 -2l2 -2z" />
                      </svg>
                      Pediatric Precautions
                    </a>
                    <a href="#geriatric-precautions-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-old"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M11 21l-1 -4l-2 -3v-6" />
                        <path d="M5 14l-1 -3l4 -3l3 2l3 .5" />
                        <path d="M8 4m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                        <path d="M7 17l-2 4" />
                        <path d="M16 21v-8.5a1.5 1.5 0 0 1 3 0v.5" />
                      </svg>
                      Geriatric Precautions
                    </a>
                    <a href="#label-warning-info-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-tag"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                        <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z"></path>
                      </svg>
                      Label Warnings
                    </a>
                    <a href="#drug-drug-interactions-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="icon icon-tabler icons-tabler-filled icon-tabler-pill"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M20.207 3.793a5.95 5.95 0 0 1 0 8.414l-8 8a5.95 5.95 0 0 1 -8.414 -8.414l8 -8a5.95 5.95 0 0 1 8.414 0m-7 1.414l-4.294 4.293l5.586 5.586l4.294 -4.292a3.95 3.95 0 1 0 -5.586 -5.586"></path>
                      </svg>
                      Drug-Drug Interactions
                    </a>
                    <a href="#drug-food-interactions-section">
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_55_2858)">
                          <path
                            d="M3 5.25C3 5.84674 3.23705 6.41903 3.65901 6.84099C4.08097 7.26295 4.65326 7.5 5.25 7.5C5.84674 7.5 6.41903 7.26295 6.84099 6.84099C7.26295 6.41903 7.5 5.84674 7.5 5.25C7.5 4.65326 7.26295 4.08097 6.84099 3.65901C6.41903 3.23705 5.84674 3 5.25 3C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25Z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11 7.25C11 7.84674 11.2371 8.41903 11.659 8.84099C12.081 9.26295 12.6533 9.5 13.25 9.5C13.8467 9.5 14.419 9.26295 14.841 8.84099C15.2629 8.41903 15.5 7.84674 15.5 7.25C15.5 6.65326 15.2629 6.08097 14.841 5.65901C14.419 5.23705 13.8467 5 13.25 5C12.6533 5 12.081 5.23705 11.659 5.65901C11.2371 6.08097 11 6.65326 11 7.25Z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6 11.25C6 11.8467 6.23705 12.419 6.65901 12.841C7.08097 13.2629 7.65326 13.5 8.25 13.5C8.84674 13.5 9.41903 13.2629 9.84099 12.841C10.2629 12.419 10.5 11.8467 10.5 11.25C10.5 10.6533 10.2629 10.081 9.84099 9.65901C9.41903 9.23705 8.84674 9 8.25 9C7.65326 9 7.08097 9.23705 6.65901 9.65901C6.23705 10.081 6 10.6533 6 11.25Z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9.25 14.75C9.25 15.3467 9.48705 15.919 9.90901 16.341C10.331 16.7629 10.9033 17 11.5 17C12.0967 17 12.669 16.7629 13.091 16.341C13.5129 15.919 13.75 15.3467 13.75 14.75C13.75 14.1533 13.5129 13.581 13.091 13.159C12.669 12.7371 12.0967 12.5 11.5 12.5C10.9033 12.5 10.331 12.7371 9.90901 13.159C9.48705 13.581 9.25 14.1533 9.25 14.75Z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_55_2858">
                            <rect width={18} height={18} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Drug-Food Interactions
                    </a>
                    <a href="#drug-disease-contra-indication-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-plus"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"></path>
                        <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"></path>
                        <path d="M10 14h4" />
                        <path d="M12 12v4" />
                      </svg>
                      Drug-Disease Contraindications
                    </a>
                    <a href="#ingredients-section">
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_55_2827)">
                          <path
                            d="M3.93549 15.5479H9.74194"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.83871 11.6768V15.5477"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.84 3.93555L11.6774 8.15842C11.6774 10.2783 9.51129 11.6775 6.83871 11.6775C4.16613 11.6775 2 10.2783 2 8.15842L2.84 3.93555"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2.96774 3.75142C2.96774 4.13641 3.37558 4.50563 4.10152 4.77786C4.82747 5.05009 5.81207 5.20303 6.83871 5.20303C7.86536 5.20303 8.84995 5.05009 9.5759 4.77786C10.3018 4.50563 10.7097 4.13641 10.7097 3.75142C10.7097 3.36643 10.3018 2.9972 9.5759 2.72497C8.84995 2.45274 7.86536 2.2998 6.83871 2.2998C5.81207 2.2998 4.82747 2.45274 4.10152 2.72497C3.37558 2.9972 2.96774 3.36643 2.96774 3.75142Z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.0274 2.96777V15.3065M12.85 2.96777V5.14519C12.85 5.72268 13.0794 6.27652 13.4878 6.68486C13.8961 7.09321 14.4499 7.32261 15.0274 7.32261C15.6049 7.32261 16.1587 7.09321 16.5671 6.68486C16.9754 6.27652 17.2048 5.72268 17.2048 5.14519V2.96777"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_55_2827">
                            <rect width={18} height={18} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Ingredients
                    </a>
                    <a href="#most-common-brands-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-forms"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 3a3 3 0 0 0 -3 3v12a3 3 0 0 0 3 3" />
                        <path d="M6 3a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3" />
                        <path d="M13 7h7a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-7" />
                        <path d="M5 7h-1a1 1 0 0 0 -1 1v8a1 1 0 0 0 1 1h1" />
                        <path d="M17 12h.01" />
                        <path d="M13 12h.01" />
                      </svg>
                      Most Common Brands
                    </a>
                    <a href="#therapeutic-alternatives-section">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-search"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                        <path d="M21 21l-6 -6" />
                      </svg>
                      Therapeutic Alternatives
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseTwo"
                  aria-expanded="false"
                  aria-controls="collapseTwo"
                  onClick={() => {
                    setActiveSection('patient');
                    window.scrollTo(0, 0);
                  }}
                >
                  Patient Education
                </button>
              </h2>
              <div
                id="collapseTwo"
                className="accordion-collapse collapse"
                data-bs-parent="#accordionExample"
              >
                <div className="accordion-body">
                  <div className=" page-links">
                    <div className="cst-overflow-links">
                      <a href="#disclaimer-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-info-circle"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                          <path d="M12 9h.01" />
                          <path d="M11 12h1v4h1" />
                        </svg>
                        Disclaimer
                      </a>
                      <a href="#disclaimer-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                          <path d="M13.5 6.5l4 4" />
                        </svg>
                        Title
                      </a>
                      <a href="#disclaimer-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-volume"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M15 8a5 5 0 0 1 0 8" />
                          <path d="M17.7 5a9 9 0 0 1 0 14" />
                          <path d="M6 15h-2a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h2l3.5 -4.5a.8 .8 0 0 1 1.5 .5v14a.8 .8 0 0 1 -1.5 .5l-3.5 -4.5" />
                        </svg>
                        Phonetic Pronounciation
                      </a>
                      <a href="#disclaimer-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-forms"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 3a3 3 0 0 0 -3 3v12a3 3 0 0 0 3 3" />
                          <path d="M6 3a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3" />
                          <path d="M13 7h7a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-7" />
                          <path d="M5 7h-1a1 1 0 0 0 -1 1v8a1 1 0 0 0 1 1h1" />
                          <path d="M17 12h.01" />
                          <path d="M13 12h.01" />
                        </svg>{' '}
                        Common Brand Name
                      </a>
                      <a href="#warning-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-tag"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                          <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z" />
                        </svg>
                        Warnings
                      </a>
                      <a href="#uses-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-report-medical"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                          <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                          <path d="M10 14l4 0" />
                          <path d="M12 12l0 4" />
                        </svg>
                        Uses
                      </a>
                      <a href="#how-to-use-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-list-check"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3.5 5.5l1.5 1.5l2.5 -2.5" />
                          <path d="M3.5 11.5l1.5 1.5l2.5 -2.5" />
                          <path d="M3.5 17.5l1.5 1.5l2.5 -2.5" />
                          <path d="M11 6l9 0" />
                          <path d="M11 12l9 0" />
                          <path d="M11 18l9 0" />
                        </svg>
                        How to Use
                      </a>
                      <a href="#education-side-effects-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-bolt"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />
                        </svg>
                        Side Effects
                      </a>
                      <a href="#precautions-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-alert-hexagon"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M19.875 6.27c.7 .398 1.13 1.143 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
                          <path d="M12 8v4" />
                          <path d="M12 16h.01" />
                        </svg>
                        Precautions
                      </a>
                      <a href="#drug-interactions-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="icon icon-tabler icons-tabler-filled icon-tabler-pill"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M20.207 3.793a5.95 5.95 0 0 1 0 8.414l-8 8a5.95 5.95 0 0 1 -8.414 -8.414l8 -8a5.95 5.95 0 0 1 8.414 0m-7 1.414l-4.294 4.293l5.586 5.586l4.294 -4.292a3.95 3.95 0 1 0 -5.586 -5.586" />
                        </svg>
                        Drug Interactions
                      </a>
                      <a href="#overdose-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-bucket-droplet"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M5 16l1.465 1.638a2 2 0 1 1 -3.015 .099l1.55 -1.737z" />
                          <path d="M13.737 9.737c2.299 -2.3 3.23 -5.095 2.081 -6.245c-1.15 -1.15 -3.945 -.217 -6.244 2.082c-2.3 2.299 -3.231 5.095 -2.082 6.244c1.15 1.15 3.946 .218 6.245 -2.081z" />
                          <path d="M7.492 11.818c.362 .362 .768 .676 1.208 .934l6.895 4.047c1.078 .557 2.255 -.075 3.692 -1.512c1.437 -1.437 2.07 -2.614 1.512 -3.692c-.372 -.718 -1.72 -3.017 -4.047 -6.895a6.015 6.015 0 0 0 -.934 -1.208" />
                        </svg>
                        Overdose
                      </a>
                      <a href="#missed-dose-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-cancel"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                          <path d="M18.364 5.636l-12.728 12.728" />
                        </svg>
                        Missed Dose
                      </a>

                      <a href="#storage-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-filled icon-tabler-flask"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M15 2a1 1 0 0 1 0 2v4.826l3.932 10.814l.034 .077a1.7 1.7 0 0 1 -.002 1.193l-.07 .162a1.7 1.7 0 0 1 -1.213 .911l-.181 .017h-11l-.181 -.017a1.7 1.7 0 0 1 -1.285 -2.266l.039 -.09l3.927 -10.804v-4.823a1 1 0 1 1 0 -2h6zm-2 2h-2v4h2v-4z" />
                        </svg>
                        Storage
                      </a>
                      <a href="#notes-section">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-note"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M13 20l7 -7" />
                          <path d="M13 20v-6a1 1 0 0 1 1 -1h6v-7a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7" />
                        </svg>
                        Notes
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="innerpage drug-detailed-page">
          <h4 className="mobile-title-text text-truncate">
            <span className="text-primary">
              {capitalizeFirstLetter(
                drugData.Basic_Drug_Info.DispensableGenericDesc,
              )}
            </span>
          </h4>

          <div className="mobile-tabs drug-search-tabs">
            <ul
              className="nav nav-pills nav-justified  flex-row"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="pills-home-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-drug-search-general"
                  type="button"
                  role="tab"
                  aria-controls="pills-home"
                  aria-selected="true"
                  onClick={() => {
                    setActiveSection('general');
                  }}
                >
                  General Information
                </button>
              </li>

              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  id="pills-drug-costs-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-drug-search-patient"
                  type="button"
                  role="tab"
                  aria-controls="pills-drug-costs"
                  aria-selected="true"
                  onClick={() => {
                    setActiveSection('patient');
                  }}
                >
                  Patient Education
                </button>
              </li>
            </ul>
          </div>
          <div className="drug-seach-result drug-search-detail-content">
            {activeSection === 'patient' ? (
              <PatientEducation patientInfo={drugData.Patient_Education_Info} />
            ) : (
              <div className=" ">
                <BasicDrugInfo
                  ref={basicDrugInfoRef}
                  basicDrugInfo={drugData.Basic_Drug_Info}
                  patientEducation={drugData.Patient_Education_Info}
                />
                <Indications
                  ref={indicationsRef}
                  indications={drugData.Indication_Links.Indications}
                />
                <SideEffects
                  ref={sideEffectsRef}
                  sideEffects={drugData.Side_Effects}
                />
                <PregnancyPrecautions
                  ref={pregnancyPrecautionsRef}
                  pregnancyPrecautions={drugData.Pregnancy_Precautions}
                />
                <LactationPrecautions
                  ref={lactationPrecautionsRef}
                  lactationPrecautions={drugData.Lactation_Precautions}
                />
                <PediatricPrecautions
                  ref={pediatricPrecautionsRef}
                  pediatricPrecautions={drugData.Pediatric_Precautions}
                />
                <GeriatricPrecautions
                  ref={geriatricPrecautionsRef}
                  geriatricPrecautions={drugData.Geriatric_Precautions}
                />
                <LabelWarningInfo
                  ref={labelWarningInfoRef}
                  labelWarnings={drugData.Label_Warning_info}
                />
                <DrugDrugInteractions
                  ref={drugDrugInteractionsRef}
                  drugDrugInteractions={drugData.Drug_Drug_Interactions_list}
                />
                <DrugFoodInteractions
                  ref={drugFoodInteractionsRef}
                  drugFoodInteractions={drugData.Drug_Food_Interactions}
                />
                <DrugDiseaseContraIndication
                  ref={drugDiseaseContraIndicationRef}
                  drugDiseaseContraIndication={
                    drugData.Drug_Disease_Contraindication_list
                  }
                />
                <Ingredients
                  ref={ingredientsRef}
                  ingredients={drugData.Basic_Drug_Info.Ingredients}
                />
                <MostCommonBrands
                  ref={mostCommonBrandsRef}
                  mostCommonBrands={drugData.Most_Common_Brands}
                />
                <TherapeuticAlternatives
                  ref={therapeuticAlternativesRef}
                  therapeuticAlternatives={drugData.Therapeutic_Alternatives}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DrugInformation;
